import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ExperimentStore } from 'bernie-core';
import { LocalizedComponentProps } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { ReviewsFilterOptionsModel } from 'models/common';

import { UitkPill } from 'uitk-react-pill';
import { UitkSheet } from 'uitk-react-sheet';

import { ReviewsFilterOptions } from 'components/infosite/property-details/reviews-filter-options.tsx';

export interface ReviewsFilterProps extends LocalizedComponentProps {
  className?: string;
  applyReviewsFilter?: (values, shouldFetch) => void;
  checkboxOptions?: ReviewsFilterOptionsModel[];
  radioOptions?: ReviewsFilterOptionsModel[];
  countSelected?: number;
  languageSelected?: string;
  label?: string;
  showSheet?: boolean;
  toggleSheet?: () => void;
  radioGroupName?: string;
  reviewLanguageFilters?: string;
  experiment?: ExperimentStore;
}

@inject('experiment')
@observer
export class ReviewsFilter extends React.Component<ReviewsFilterProps> {
  public constructor(props) {
    super(props);

    this.setFilters = this.setFilters.bind(this);
  }

  public componentWillUnmount() {
    const { applyReviewsFilter } = this.props;

    if (applyReviewsFilter) {
      applyReviewsFilter([], false);
    }
  }

  public setFilters(scoreFilters) {
    const { applyReviewsFilter, toggleSheet } = this.props;

    if (applyReviewsFilter) {
      applyReviewsFilter(scoreFilters, true);
    }

    if (toggleSheet) {
      toggleSheet();
    }
  }

  public render() {
    const {
      countSelected,
      languageSelected,
      className,
      checkboxOptions,
      label,
      showSheet,
      toggleSheet,
      radioGroupName,
      radioOptions,
      reviewLanguageFilters,
      experiment
    } = this.props;
    const hasLanguageSelected = radioOptions && radioOptions.length > 0 && languageSelected !== radioOptions[0].label;
    const showActive = countSelected > 0 || hasLanguageSelected;
    const countToDisplay = showActive ? ` (${countSelected})` : '';
    const languageToDisplay = languageSelected ? ` (${languageSelected})` : '';
    const languageFilterBucket =
      experiment.exposures.PWA_HIS_DATELESS_PDP_Reviews_language_filter &&
      experiment.exposures.PWA_HIS_DATELESS_PDP_Reviews_language_filter.bucket;

    if (radioOptions && radioOptions.length > 2) {
      return (
        <AbacusExperiment name="PWA_HIS_DATELESS_PDP_Reviews_language_filter">
          <AbacusControl>
            <DefaultReviewFilter
              className={className}
              checkboxOptions={checkboxOptions}
              label={label}
              showSheet={showSheet}
              toggleSheet={toggleSheet}
              showActive={showActive}
              setFilters={this.setFilters}
              countToDisplay={countToDisplay}
            />
          </AbacusControl>
          <AbacusVariant bucket={1}>
            <LanguageFilterExperiment
              className={className}
              label={label}
              languageToDisplay={languageToDisplay}
              radioGroupName={radioGroupName}
              radioOptions={radioOptions}
              reviewLanguageFilters={reviewLanguageFilters}
              setFilters={this.setFilters}
              showActive={showActive}
              showSheet={showSheet}
              toggleSheet={toggleSheet}
            />
          </AbacusVariant>
        </AbacusExperiment>
      );
    }

    if (radioOptions && languageFilterBucket === 1) {
      return (
        <LanguageFilterExperiment
          className={className}
          label={label}
          languageToDisplay={languageToDisplay}
          radioGroupName={radioGroupName}
          radioOptions={radioOptions}
          reviewLanguageFilters={reviewLanguageFilters}
          setFilters={this.setFilters}
          showActive={showActive}
          showSheet={showSheet}
          toggleSheet={toggleSheet}
        />
      );
    }

    return (
      <DefaultReviewFilter
        className={className}
        checkboxOptions={checkboxOptions}
        label={label}
        showSheet={showSheet}
        toggleSheet={toggleSheet}
        showActive={showActive}
        setFilters={this.setFilters}
        countToDisplay={countToDisplay}
      />
    );
  }
}

export interface DefaultReviewFilterProps {
  className?: string;
  checkboxOptions?: ReviewsFilterOptionsModel[];
  label?: string;
  showSheet?: boolean;
  showActive?: boolean;
  toggleSheet?: () => void;
  setFilters?: (values) => void;
  countToDisplay?: string;
}

export const DefaultReviewFilter: React.FunctionComponent<DefaultReviewFilterProps> = props => {
  const { className, checkboxOptions, label, showSheet, toggleSheet, showActive, setFilters, countToDisplay } = props;

  if (!checkboxOptions) {
    return null;
  }

  return (
    <span className={className}>
      <Viewport>
        <ViewSmall>
          <>
            <span className="all-cell-shrink" onClick={toggleSheet}>
              <UitkPill checked={!showSheet && showActive} triggerPill={true} className="all-b-padding-six" active={showSheet}>
                <span className="all-r-padding-two">{label + countToDisplay}</span>
              </UitkPill>
            </span>
            <UitkSheet isVisible={showSheet} className="uitk-grid m-x-padding-three uitk-scroll-vertical">
              {showSheet && (
                <ReviewsFilterOptions
                  showButtons={true}
                  applyOptionsCallback={setFilters}
                  className="all-cell-fill"
                  checkboxOptions={checkboxOptions}
                  label={label}
                  toggleSheet={toggleSheet}
                />
              )}
            </UitkSheet>
          </>
        </ViewSmall>
        <ViewLarge>
          <ReviewsFilterOptions showButtons={false} applyOptionsCallback={setFilters} checkboxOptions={checkboxOptions} label={label} />
        </ViewLarge>
      </Viewport>
    </span>
  );
};
DefaultReviewFilter.displayName = 'DefaultReviewFilter';

interface LanguageFilterExperimentProps extends DefaultReviewFilterProps {
  languageToDisplay?: string;
  radioGroupName: string;
  radioOptions: ReviewsFilterOptionsModel[];
  reviewLanguageFilters?: string;
}

export const LanguageFilterExperiment: React.FunctionComponent<LanguageFilterExperimentProps> = props => {
  const {
    className,
    label,
    languageToDisplay,
    radioGroupName,
    radioOptions,
    reviewLanguageFilters,
    showActive,
    showSheet,
    toggleSheet,
    setFilters
  } = props;

  return (
    <span className={className}>
      <Viewport>
        <ViewSmall>
          <>
            <span className="all-cell-shrink" onClick={toggleSheet}>
              <UitkPill checked={!showSheet && showActive} triggerPill={true} className="all-b-padding-six" active={showSheet}>
                <span className="all-r-padding-two">{label + languageToDisplay}</span>
              </UitkPill>
            </span>
            <UitkSheet isVisible={showSheet} className="uitk-grid m-x-padding-three uitk-scroll-vertical">
              {showSheet && (
                <ReviewsFilterOptions
                  showButtons={true}
                  applyOptionsCallback={setFilters}
                  className="all-cell-fill"
                  label={label}
                  radioGroupName={radioGroupName}
                  radioOptions={radioOptions}
                  reviewLanguageFilters={reviewLanguageFilters}
                  toggleSheet={toggleSheet}
                />
              )}
            </UitkSheet>
          </>
        </ViewSmall>
        <ViewLarge>
          <ReviewsFilterOptions
            showButtons={false}
            applyOptionsCallback={setFilters}
            label={label}
            radioGroupName={radioGroupName}
            radioOptions={radioOptions}
          />
        </ViewLarge>
      </Viewport>
    </span>
  );
};
LanguageFilterExperiment.displayName = 'LanguageFilterExperiment';
